import { useCallback, useEffect, useState } from 'react'
import { ITableDataProvider } from '../types/data-provider.types'

type UseTableDataState<UiRow> = {
  rows: UiRow[]
  isLoading: boolean
  error: string | null
}

type UseTableDataReturn<UiRow, CreatePayload, UpdatePayload> = UseTableDataState<UiRow> & {
  refetch: () => Promise<void>
  createRow: (payload: CreatePayload) => Promise<void>
  updateRow: (id: number | string, payload: UpdatePayload) => Promise<void>
  removeRow: (id: number | string) => Promise<void>
}

const useTableData = <UiRow, CreatePayload, UpdatePayload>(
  provider: ITableDataProvider<UiRow, CreatePayload, UpdatePayload>,
  getRowId: (row: UiRow) => number | string
): UseTableDataReturn<UiRow, CreatePayload, UpdatePayload> => {
  const [rows, setRows] = useState<UiRow[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const fetchRows = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await provider.getList()
      setRows(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה בטעינת הנתונים')
    } finally {
      setIsLoading(false)
    }
  }, [provider])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const createRow = useCallback(
    async (payload: CreatePayload): Promise<void> => {
      const newRow = await provider.create(payload)
      setRows((prev) => [newRow, ...prev])
    },
    [provider]
  )

  const updateRow = useCallback(
    async (id: number | string, payload: UpdatePayload): Promise<void> => {
      const updated = await provider.update(id, payload)
      const updatedId = getRowId(updated)
      setRows((prev) =>
        prev.map((row) => (getRowId(row) === updatedId ? updated : row))
      )
    },
    [provider, getRowId]
  )

  const removeRow = useCallback(
    async (id: number | string): Promise<void> => {
      await provider.remove(id)
      setRows((prev) => prev.filter((row) => getRowId(row) !== id))
    },
    [provider, getRowId]
  )

  return { rows, isLoading, error, refetch: fetchRows, createRow, updateRow, removeRow }
}

export default useTableData
